// ✅ SubscriptionPage.jsx
import { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import {
    Check,
    Crown,
    Star,
    Shield,
    Zap,
} from "lucide-react";

import { useSelector } from "react-redux";
import {
    getWithTokenAPI,
    getWithoutTokenAPI,
    postWithTokenAPI,
} from "../../util/Api";
import toast, { Toaster } from "react-hot-toast";

import Topbar from "./Topbar";
import Sidebar from "./Sidebar"; // Adjust if incorrect import
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Margin } from "@mui/icons-material";

export default function SubscriptionPage({ onNavigate }) {
    const [plandetails, setPlanDetails] = useState([]);
    const [profileData, setProfileData] = useState("");
    const [userSubscription, setUserSubscription] = useState(null);
    const [isLoadingPlans, setIsLoadingPlans] = useState(true);
    const [PaymentFile, setPayment] = useState(false)
    const [loadingPlanId, setLoadingPlanId] = useState(null);


    const user = useSelector((state) => state.user);
    const token = user?.userId?.accessToken;
    const id = user?.userId?.user?._id;

    const location = useLocation();
    const { profileId } = useParams();
    const { profileName, profileMobile, planname } = location.state || {};


    console.log(location.state, "location.state")
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await getWithTokenAPI(
                    token,
                    `/user-id/get_by_id?user_id=${id}`
                );

                const profile = response?.data?.profile;
                const sub = response?.data?.subscription;

                if (profile) setProfileData(profile.name);
                if (sub) {
                    setUserSubscription({
                        plan_id: sub.plan_id,
                        plan_price: sub.price,
                        plan_name: sub.plan_name,
                        status: sub.status,
                        amount: sub.price,
                        start_date: sub.start_date,
                        end_date: sub.end_date,
                    });
                }
            } catch (err) {
                console.log("Profile fetch failed", err);
            }
        };

        if (id && token) fetchProfile();
    }, [id, token]);

    // ✅ Fetch Plans
    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setIsLoadingPlans(true);
                const response = await getWithoutTokenAPI(`/subscriptionPlan/get_all`);

                if (response?.statusCode === 200) {
                    setPlanDetails(response.data || []);
                }
            } catch (err) {
                toast.error("Failed to load plans");
            } finally {
                setIsLoadingPlans(false);
            }
        };

        fetchPlans();
    }, []);

    // Disable lower plans
    const isPlanDisabled = (plan) => {
        if (!userSubscription) return false;
        if (userSubscription.plan_name === plan.plan_name) return true;
        if (userSubscription.plan_price >= plan.price) return true;
        return false;
    };

    // Button Text
    const getButtonText = (plan) => {
        if (userSubscription?.plan_name === plan.plan_name) return "Current Plan";
        if (userSubscription?.plan_price > plan.price) return plan.plan_name;
        if (plan.price > userSubscription?.plan_price)
            return `Upgrade to ${plan.plan_name}`;
        return `Choose ${plan.plan_name}`;
    };

    // Date Format
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleDateString("en-US", { month: "long" });
        const year = date.getFullYear();

        const suffix =
            day > 3 && day < 21
                ? "th"
                : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";

        return `${day}${suffix} ${month} ${year}`;
    };


    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleSubscribe = async (plan) => {
        // 1️⃣ Already subscribed
        if (PaymentFile) {
            toast.error("You are already subscribed to this plan or a higher one.");
            return;
        }

        // 2️⃣ Check lower or higher plan
        if (isPlanDisabled(plan)) {
            toast.error("You are already subscribed to this plan or a higher one.");
            return;
        }

        // 3️⃣ Load Razorpay script
        const res = await loadRazorpayScript();
        if (!res) {
            toast.error("Razorpay SDK failed to load.");
            return;
        }

        setPayment(true);
        setLoadingPlanId(plan._id); // spinner

        try {
            const amountInPaise = plan.price * 100;

            const orderRes = await postWithTokenAPI(token, "/payment/create-order", {
                plan_id: plan._id,
                user_id: profileId,
                amount: amountInPaise,
            });

            const order = orderRes?.data?.order;
            if (!order) {
                setPayment(false);
                setLoadingPlanId(null);
                return toast.error("Order creation failed.");
            }

            const options = {
                key: "rzp_test_RWmtGzl0MMInyw",
                amount: order.amount,
                currency: order.currency,
                name: "AvanAval Matrimony",
                description: `${plan.plan_name} Subscription`,
                order_id: order.id,

                prefill: {
                    name: profileName || "User",
                    email: "user@example.com",
                    contact: profileMobile || "9999999999",
                },

                theme: { color: "#e11d48" },
                handler: async (response) => {
                    try {
                        const verify = await postWithTokenAPI(token, "/payment/verify", {
                            payment_id: response.razorpay_payment_id,
                            order_id: response.razorpay_order_id,
                            signature: response.razorpay_signature,
                            plan_id: plan._id,
                            user_id: profileId,
                        });

                        if (verify?.statusCode === 200) {
                            toast.success(" Subscription successful!");
                            setPayment(false);

                            // Refresh subscription
                            const updated = await getWithTokenAPI(
                                token,
                                `/user-id/get_by_id?user_id=${profileId}`
                            );

                            const newSub = updated?.data?.subscription;

                            setUserSubscription({
                                plan_id: newSub?._id,
                                plan_name: newSub?.plan_name,
                                plan_price: newSub?.plan_price,
                                status: newSub?.status,
                                amount: newSub?.plan_price,
                                start_date: newSub?.start_date,
                                end_date: newSub?.end_date,
                            });

                            navigate("/admin/user-management");
                        } else {
                            toast.error("Payment verification failed.");
                            setPayment(false);
                        }
                    } catch (err) {
                        console.error("Verify Error:", err);
                        toast.error("Payment verification error.");
                        setPayment(false);
                    }
                },

                // 7️⃣ User closes Razorpay popup
                modal: {
                    ondismiss: () => {
                        setPayment(false);
                        toast.error("Payment cancelled by user.");
                    },
                },
            };

            // 8️⃣ Init Razorpay
            const rzp = new window.Razorpay(options);

            // 9️⃣ On Payment Failed
            rzp.on("payment.failed", async (response) => {
                setPayment(false);

                toast.error(
                    response.error.description || ("Payment failed.")
                );

                try {
                    await postWithTokenAPI(token, "/payment/verify", {
                        payment_id: response.error.metadata.payment_id,
                        order_id: response.error.metadata.order_id,
                        signature: response.error.metadata.payment_id,
                        status: "failed",
                    });
                } catch (err) {
                    console.error("Failed payment update:", err);
                }
            });

            rzp.open();

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong.");
            setPayment(false);
        } finally {
            setLoadingPlanId(null);
        }
    };


    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState("subscriptions");
    const handleSectionChange = (section) => {
        setActiveSection(section);

        switch (section) {
            case "dashboard":
                navigate("/admin/dashboard");
                break;
            case "users":
                navigate("/admin/user-management");
                break;
            case "approvals":
                navigate("/admin/content-approvals");
                break;
            case "reports":
                navigate("/admin/reports-moderation");
                break;
            case "subscriptions":
                navigate("/admin/subscription-plans");
                break;
            case "cms":
                navigate("/admin/content-management");
                break;
            case "payments":
                navigate("/admin/payment-list");
                break;
            case "user-profile":
                // Don't navigate away from current user profile
                if (!location.pathname.includes("/admin/user-profile/")) {
                    navigate("/admin/user-management");
                }
                break;
            default:
                navigate("/admin/dashboard");
        }
    };
    const isCurrentPlan = (plan) => {
        return plan.plan_name === planname;
    };

    const isLowerPlan = (plan) => {
        if (!planname) return false; // If no active plan, nothing is disabled

        return Number(plan.price) < Number(currentPlanPrice);
    };

    const currentPlanPrice = plandetails.find(p => p.plan_name === planname)?.price || 0;



    return (
        <div className="admin-layout-container">
            <Topbar />
            <Toaster position="top-right" />

            <div className="admin-main-area">
                <Sidebar activeSection={activeSection}
                    setActiveSection={handleSectionChange} />

                <div className="admin-content-area">
                    <div className="admin-scrollable-content">
                        <div className="max-w-7xl mx-auto px-4 py-10">
                            <div className="text-center mb-16">
                                <h1 className="text-4xl font-bold mb-4">
                                    Find Your Perfect Tamil Life Partner
                                </h1>
                                <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
                                    Join thousands of Tamil families who found happiness through our
                                    premium matrimony services.
                                </p>

                                <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4" />
                                        100% Verified Profiles
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Star className="w-4 h-4" />
                                        Tamil Community Focus
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-4 h-4" />
                                        Fast Matching Algorithm
                                    </div>
                                </div>
                                {profileName && (
                                    <div className=" text-lg font-semibold text-gray-700 relative z-10"
                                        style={{ margin: "30px" }}>
                                        Managing Subscription For: <span className="text-pink-600">{profileName}</span>
                                    </div>
                                )}
                            </div>

                            {planname && (
                                <Card className=" bg-green-50 border-green-300"
                                    style={{ marginBottom: "30px" }}
                                >
                                    <CardContent className="p-6 flex items-center gap-4">
                                        <Crown className="h-8 w-8 text-green-700" />
                                        <div>
                                            <h3 className="text-lg font-semibold text-green-800">
                                                Current Plan: {planname}
                                            </h3>

                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gap: "20px",
                            }}>

                                {plandetails.map((plan) => (
                                    <Card
                                        key={plan._id}
                                        className={`relative ${plan.plan_name === "Free"
                                            ? "border-gray-500"
                                            : plan.plan_name === "Premium"
                                                ? "border-blue-500"
                                                : plan.plan_name === "Elite"
                                                    ? "border-purple-500"
                                                    : "border-red-500"
                                            } ${isPlanDisabled(plan) ? "opacity-50" : ""}`}
                                    >
                                        <CardHeader className="pb-4">
                                            <CardTitle
                                                className={`text-2xl font-bold ${plan.plan_name === "Free"
                                                    ? "text-gray-600"
                                                    : plan.plan_name === "Premium"
                                                        ? "text-blue-600"
                                                        : plan.plan_name === "Elite"
                                                            ? "text-purple-600"
                                                            : "text-red-600"
                                                    }`}
                                            >
                                                {plan.plan_name}
                                            </CardTitle>
                                        </CardHeader>

                                        <CardContent className="space-y-4">
                                            <div className="mt-4">
                                                <span className="text-4xl font-bold">
                                                    {plan.price === 0 ? "Free" : `₹${plan.price}`}
                                                </span>
                                            </div>

                                            <ul className="space-y-3">
                                                {plan.features?.map((feature, i) => (
                                                    <li key={i} className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-green-500" /> {feature}
                                                    </li>
                                                ))}
                                            </ul>

                                            <Button
                                                className={`w-full mt-6 cursor-pointer 
      ${isCurrentPlan(plan)
                                                        ? "bg-blue-600 cursor-not-allowed"
                                                        : plan.plan_name === "Free"
                                                            ? "bg-gray-600 hover:bg-gray-700"
                                                            : plan.plan_name === "Premium"
                                                                ? "bg-blue-600 hover:bg-blue-700"
                                                                : plan.plan_name === "Elite"
                                                                    ? "bg-purple-600 hover:bg-purple-700"
                                                                    : "bg-gradient-to-br from-red-500 to-pink-600"
                                                    } text-white`}
                                                onClick={() => handleSubscribe(plan)}
                                                disabled={isCurrentPlan(plan) || isLowerPlan(plan) || loadingPlanId === plan._id}
                                            >
                                                {isCurrentPlan(plan)
                                                    ? "Active"
                                                    : loadingPlanId === plan._id
                                                        ? "Processing..."
                                                        : getButtonText(plan)}
                                            </Button>

                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
